import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2, FileEdit, Calendar, FileDown } from "lucide-react";
import { toast } from "sonner";
import EvolutionTemplatePicker from "@/components/EvolutionTemplatePicker";
import { generateEvolutionPdf } from "@/lib/evolutionPdf";
import { useBranding } from "@/context/BrandingContext";

function todayStr() { return new Date().toISOString().slice(0, 10); }

export default function EvolucionesList({ patientId }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [patient, setPatient] = useState(null);
  const [form, setForm] = useState({ fecha: todayStr(), diente: "", diagnostico: "", procedimiento: "", observaciones: "" });
  const { branding } = useBranding();

  const load = async () => {
    try {
      const { data } = await api.get(`/patients/${patientId}/evolutions`);
      setItems(data);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, [patientId]); // eslint-disable-line
  useEffect(() => {
    api.get(`/patients/${patientId}`).then((r) => setPatient(r.data)).catch(() => {});
  }, [patientId]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/patients/${patientId}/evolutions`, form);
      toast.success("Evolución registrada");
      setForm({ fecha: todayStr(), diente: "", diagnostico: "", procedimiento: "", observaciones: "" });
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (eid) => {
    if (!window.confirm("¿Eliminar evolución?")) return;
    try { await api.delete(`/evolutions/${eid}`); load(); }
    catch (err) { toast.error("No se pudo eliminar"); }
  };

  const exportPdf = (it) => {
    try {
      generateEvolutionPdf({ evolution: it, patient, branding });
    } catch (e) {
      toast.error("No se pudo generar el PDF");
    }
  };

  return (
    <div className="space-y-4" data-testid="evoluciones-tab">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl tracking-tight">Evoluciones clínicas</h2>
          <p className="text-sm text-muted-foreground">Notas detalladas por visita o procedimiento.</p>
        </div>
        <button onClick={() => setOpen((v) => !v)} className="btn-primary" data-testid="new-evolucion-btn">
          <Plus className="w-4 h-4" /> {open ? "Cancelar" : "Nueva evolución"}
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="card-flat p-5 space-y-3" data-testid="evolucion-form">
          <div className="flex justify-end -mb-1">
            <EvolutionTemplatePicker
              onPick={(t) => setForm((prev) => ({
                ...prev,
                diagnostico: t.diagnostico || prev.diagnostico,
                procedimiento: t.procedimiento,
                observaciones: t.observaciones || prev.observaciones,
              }))}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <label className="label-eyebrow block mb-1">Fecha *</label>
              <input type="date" required className="input-flat font-mono" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Diente (FDI)</label>
              <input className="input-flat font-mono" placeholder="Ej. 16" value={form.diente} onChange={(e) => setForm({ ...form, diente: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow block mb-1">Diagnóstico</label>
              <input className="input-flat" value={form.diagnostico} onChange={(e) => setForm({ ...form, diagnostico: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Procedimiento realizado *</label>
            <textarea rows={2} required className="input-flat h-auto py-2" value={form.procedimiento} onChange={(e) => setForm({ ...form, procedimiento: e.target.value })} data-testid="evolucion-procedimiento" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Observaciones</label>
            <textarea rows={2} className="input-flat h-auto py-2" value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
          </div>
          <button type="submit" className="btn-primary" data-testid="evolucion-save">Guardar evolución</button>
        </form>
      )}

      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="card-flat p-12 text-center text-sm text-muted-foreground">Sin evoluciones registradas.</div>
        ) : items.map((it) => (
          <div key={it.id} className="card-flat p-4 border-l-4 border-l-primary" data-testid={`evol-${it.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="font-mono text-xs text-muted-foreground">{it.fecha}</span>
                  {it.diente && <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm bg-primary/15 text-primary">D · {it.diente}</span>}
                  {it.doctor && (it.doctor.name || it.doctor.registry_number) && (
                    <span className="text-xs text-muted-foreground">
                      por <span className="font-semibold text-foreground/80">{it.doctor.name}</span>
                      {it.doctor.specialty && <span> · {it.doctor.specialty}</span>}
                      {it.doctor.registry_number && <span className="font-mono"> · {it.doctor.registry_number}</span>}
                    </span>
                  )}
                </div>
                {it.diagnostico && (
                  <div className="text-sm mb-2"><span className="font-bold">Diagnóstico:</span> {it.diagnostico}</div>
                )}
                <div className="text-sm whitespace-pre-wrap">{it.procedimiento}</div>
                {it.observaciones && (
                  <div className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap"><span className="font-bold">Obs:</span> {it.observaciones}</div>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => exportPdf(it)} className="p-2 text-muted-foreground hover:text-primary" title="Exportar PDF" data-testid={`evol-pdf-${it.id}`}>
                  <FileDown className="w-4 h-4" />
                </button>
                <button onClick={() => remove(it.id)} className="p-2 text-muted-foreground hover:text-destructive" title="Eliminar">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
